import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Award, Download, Lock, CheckCircle2, Eye, Medal, Trophy, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Certificate from "@/components/Certificate";
import Badge from "@/components/Badge";

export default function Certificates() {
  const [data, setData] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);
  const [showCert, setShowCert] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const badgeRef = useRef(null);
  const certRef = useRef(null);

  const load = () => api.get("/achievements").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const capture = async (node, filename) => {
    setDownloading(true);
    try {
      let url;
      try { url = await toPng(node, { pixelRatio: 2, cacheBust: true }); }
      catch { url = await toPng(node, { pixelRatio: 2, cacheBust: true, skipFonts: true }); }
      const link = document.createElement("a");
      link.download = filename; link.href = url; link.click();
      toast.success("Downloaded");
    } catch { toast.error("Could not generate image. Please try again."); }
    finally { setDownloading(false); }
  };

  const downloadBadge = async (lab) => {
    if (!badgePreview || badgePreview.id !== lab.id) { setBadgePreview(lab); await new Promise((r) => setTimeout(r, 400)); }
    if (badgeRef.current) await capture(badgeRef.current, `HMG-Badge-${lab.badge_title.replace(/\s+/g, "-")}.png`);
  };
  const downloadCert = async () => {
    if (!showCert) { setShowCert(true); await new Promise((r) => setTimeout(r, 400)); }
    if (certRef.current) await capture(certRef.current, "HMG-Linux-Hands-On-Lab-Certificate.png");
  };

  if (!data) return <div className="p-10 text-muted-foreground">Loading achievements…</div>;

  const s = data.stats;
  const certAwarded = data.certificate.status === "Awarded";

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="achievements-page">
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-primary" />
        <h1 className="font-display font-bold text-3xl tracking-tight">Achievements</h1>
      </div>
      <p className="text-muted-foreground mt-1">
        Earn a badge for every hands-on lab you complete. Finish all required labs to unlock your{" "}
        <span className="font-semibold text-foreground">Linux Hands-On Lab Certificate of Completion</span>.
      </p>

      {/* Progress summary */}
      <div className="mt-6 rounded-xl border border-border bg-card p-6" data-testid="achievement-summary">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Linux Hands-On Lab Progress</div>
            <div className="font-display font-bold text-2xl mt-1">{s.completed} of {s.required} Labs Completed</div>
          </div>
          <div className="flex gap-8">
            <div className="text-center"><div className="font-display font-bold text-2xl text-primary">{s.badges_earned}</div><div className="text-xs text-muted-foreground">Badges Earned</div></div>
            <div className="text-center"><div className="font-display font-bold text-2xl">{s.pct}%</div><div className="text-xs text-muted-foreground">Program Complete</div></div>
            <div className="text-center">
              <div className={`font-display font-bold text-2xl ${certAwarded ? "text-success" : "text-muted-foreground"}`}>{certAwarded ? "Awarded" : "Locked"}</div>
              <div className="text-xs text-muted-foreground">Final Certificate</div>
            </div>
          </div>
        </div>
        <Progress value={s.pct} className="h-2.5 mt-4" data-testid="program-progress" />
      </div>

      {/* Final certificate */}
      <h2 className="font-display font-bold text-xl mt-8 mb-3 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Program Certificate</h2>
      <div className={`rounded-xl border p-6 flex items-center justify-between flex-wrap gap-4 ${certAwarded ? "border-success/40 bg-success/5" : "border-border bg-card"}`} data-testid="final-certificate-card">
        <div className="flex items-center gap-4">
          <div className={`h-14 w-14 rounded-full grid place-items-center ${certAwarded ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"}`}>
            {certAwarded ? <Award className="h-7 w-7" /> : <Lock className="h-7 w-7" />}
          </div>
          <div>
            <div className="font-display font-bold text-lg">Linux Hands-On Lab Certificate of Completion</div>
            <div className="text-sm text-muted-foreground">
              {certAwarded ? <>Certificate ID <span className="font-mono">{data.certificate.certificate_id}</span> · Verify <span className="font-mono">{data.certificate.verification_id}</span></> : `Complete all ${s.required} required labs to unlock (${s.remaining} remaining).`}
            </div>
          </div>
        </div>
        {certAwarded ? (
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setShowCert(true)} data-testid="cert-preview-btn"><Eye className="h-4 w-4" /> Preview</Button>
            <Button className="gap-2" onClick={downloadCert} disabled={downloading} data-testid="cert-download-btn"><Download className="h-4 w-4" /> Download</Button>
          </div>
        ) : (
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Locked</span>
        )}
      </div>

      {/* Badge collection */}
      <h2 className="font-display font-bold text-xl mt-8 mb-3 flex items-center gap-2"><Medal className="h-5 w-5 text-primary" /> Badge Collection ({s.badges_earned}/{s.badges_total})</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.labs.map((l) => (
          <div key={l.id} data-testid={`badge-card-${l.id}`} className={`rounded-lg border p-4 flex items-center gap-4 ${l.completed ? "border-primary/40 bg-card" : "border-border bg-card opacity-75"}`}>
            <div className={`h-16 w-16 rounded-full grid place-items-center shrink-0 text-2xl ${l.completed ? "bg-[#0b2a2e] text-white" : "bg-secondary"}`}>
              {l.completed ? "🐧" : <Lock className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Level {l.level}{l.required ? "" : " · Optional"}</div>
              <div className="font-semibold text-sm truncate">{l.badge_title}</div>
              {l.completed ? (
                <button onClick={() => setBadgePreview(l)} className="text-xs text-primary hover:underline mt-1 flex items-center gap-1" data-testid={`badge-view-${l.id}`}>
                  <CheckCircle2 className="h-3 w-3" /> Earned — view
                </button>
              ) : (
                <div className="text-xs text-muted-foreground mt-1">Complete this lab to unlock</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Badge preview dialog */}
      <Dialog open={!!badgePreview} onOpenChange={(o) => !o && setBadgePreview(null)}>
        <DialogContent className="max-w-[680px]" data-testid="badge-dialog">
          <DialogHeader><DialogTitle className="font-display">{badgePreview?.badge_title}</DialogTitle></DialogHeader>
          <div className="overflow-auto flex justify-center">
            <div style={{ width: 600 }}>
              {badgePreview && (
                <Badge ref={badgeRef} studentName={data.student_name} achievementTitle={badgePreview.badge_title} labTitle={badgePreview.lab_title} earnedAt={badgePreview.completed_at} badgeId={badgePreview.badge_id} />
              )}
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button className="gap-2" onClick={() => badgePreview && downloadBadge(badgePreview)} disabled={downloading} data-testid="badge-download-btn">
              <Download className="h-4 w-4" /> {downloading ? "Generating…" : "Download Badge"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Certificate preview dialog */}
      <Dialog open={showCert} onOpenChange={setShowCert}>
        <DialogContent className="max-w-[1060px] w-[95vw]" data-testid="cert-dialog">
          <DialogHeader><DialogTitle className="font-display">Program Certificate</DialogTitle></DialogHeader>
          <div className="overflow-auto">
            <div className="mx-auto" style={{ width: 1000 }}>
              <Certificate ref={certRef} studentName={data.student_name} program={data.program} completedAt={data.certificate.awarded_at} certificateId={data.certificate.certificate_id} verificationId={data.certificate.verification_id} />
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button className="gap-2" onClick={downloadCert} disabled={downloading} data-testid="cert-dialog-download"><Download className="h-4 w-4" /> {downloading ? "Generating…" : "Download PNG"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
